import { soqlize, toOdata, query } from '../../utils/soql-utils'
import {
  isStateTreeNode,
  getSnapshot,
  hasParent,
  getParent,
  getType,
  types as t,
  Instance
} from '@invisr/mobx-state-tree'
import crudActions from './crud-actions'
import WhereRule from './where-rule'
import uniqBy from 'lodash/uniqBy'
import { promisedComputed } from '../../utils/computed-async-mobx'
import Field from './field'
import { getTransformationFieldSets, getWhereQueryFields, getTransformationQueryFields } from '../../utils/get-fields'
import type { IObservableArray } from 'mobx'

const OrderByItem = t.model('OrderByItem', {
  field: t.maybeNull(Field),
  direction: t.optional(t.string, 'ASC'),
  nulls: t.optional(t.string, 'LAST')
}).actions(crudActions)

export const Order = t.model('Order', {
  fields: t.optional(t.array(OrderByItem), [])
}).actions(self => ({
  addField () {
    self.fields.push({})
  }
}))

export const Where = t
  .model('Where', {
    rules: t.optional(t.array(WhereRule), []),
    whereClauseOrder: t.maybeNull(t.string)
  })
  .views(self => ({
    get queryFields () {
      return getWhereQueryFields(self)
    },
    get resolved () {
      const rules = []
      getSnapshot(self.rules)
      for (const r of self.rules) {
        if (!r.rightValue) continue
        const mapper = r.rightValue.mapper
        const rightValue = mapper.toProps
        rules.push({ ...r, rightValue })
      }

      return {
        ...getSnapshot(self),
        rules
      }
    },
    async toSFasync () {
      const rules = []
      getSnapshot(self.rules)
      for (const r of self.rules) {
        if (!r.rightValue) continue
        const mapper = r.rightValue.mapper
        const rightValue = await mapper.convertProps()
        rules.push({ ...r, rightValue })
      }

      return {
        ...getSnapshot(self),
        rules
      }
    },
    get dataSourceFieldSets () {
      return getTransformationFieldSets({ where: self })
    }
  }))
  .actions(crudActions)
  .actions(self => {
    function updateRules (rules) {
      self.update({ rules })
    }
    function addRule (rule) {
      self.rules.push(rule)
    }
    function removeRule (rule) {
      self.update({
        whereClauseOrder: createWhereClauseOrder(
          self.whereClauseOrder,
          rule.ruleNumber
        )
      })

      self.rules
        .filter(r => r.ruleNumber > rule.ruleNumber)
        .forEach(higherRule => {
          const newNum = higherRule.ruleNumber - 1
          higherRule.update({ ruleNumber: newNum })
        })
      //
      self.rules.splice(self.rules.indexOf(rule), 1)
    }

    return {
      updateRules,
      addRule,
      removeRule
    }
  })
export const GroupBy = t
  .model('GroupBy', {
    aggregateFields: t.optional(t.array(Field), []),
    groupByFields: t.optional(t.array(Field), []),
    having: t.optional(Where, {})
    // having: t.maybeNull(Where)
  })
  .views(self => ({
    get validAggFields () {
      return self.aggregateFields.filter(
        field => field.fieldPath && field.fieldPath !== ''
      )
    },
    labels () {
      const aggFields = self.aggregateFields.map(agg => {
        return {
          label: agg.aliasedFieldPath,
          secondaryLabel: 'aggregate',
          meta: {
            type: 'transformer',
            groupBy: self,
            value: agg,
            fieldPath: agg.aliasedFieldPath
          },
          childNodes: []
        }
      })
      const groupFields = self.groupByFields.map(group => {
        return {
          label: group.fieldPath,
          secondaryLabel: 'grouping',
          meta: {
            type: 'transformer',
            groupBy: self,
            value: group,
            fieldPath: group.fieldPath
          },
          childNodes: []
        }
      })
      let allAggs = []
      if (self.aggregateFields.length) {
        allAggs = [
          {
            label: '[All Aggregates]',
            meta: {
              type: 'transformer',
              groupBy: self,
              value: self.aggregateFields,
              fieldPath: 'allAggregates',
              picklist: self.aggregateFields.map(a => a.aliasedFieldPath)
            },
            childNodes: []
          }
        ]
      }
      let allGroups = []
      if (self.groupByFields.length) {
        allGroups = [
          {
            label: '[All Groupings]',
            meta: {
              type: 'transformer',
              groupBy: self,
              value: self.groupByFields,
              fieldPath: 'allGroupings',
              picklist: self.groupByFields.map(g => g.fieldPath)
            },
            childNodes: []
          }
        ]
      }

      return allAggs.concat(allGroups, aggFields, groupFields)
    }
  }))
  .actions(crudActions)
  .actions(self => {
    function addAggregateField (item) {
      const emptyItem = {
        fieldPath: '',
        label: '',
        dataSourceId: ''
      }

      self.aggregateFields.push(emptyItem)
    }
    function updateAggregateField (idx, type, val) {
      self.aggregateFields[idx].update(type, val)
      self.aggregateFields.splice(idx, 1, self.aggregateFields[idx])
    }
    function removeAggregateField (item) {
      if (self.aggregateFields.length <= 1) {
        self.aggregateFields = [] as IObservableArray
      } else {
        self.aggregateFields.splice(self.aggregateFields.indexOf(item), 1)
      }
    }
    function addGroupByField (item) {
      self.groupByFields.push({ label: '', ...item })
    }
    function removeGroupByField (item) {
      self.groupByFields.splice(self.groupByFields.indexOf(item), 1)
    }
    return {
      addAggregateField,
      updateAggregateField,
      removeAggregateField,
      addGroupByField,
      removeGroupByField
    }
  })

const DataTransformer = t
  .model('DataTransformer', {
    groupBy: t.optional(GroupBy, {}),
    where: t.optional(Where, {}),
    limit: t.optional(t.number, 100),
    additionalText: t.maybeNull(t.string),
    order: t.optional(Order, {}),
    table: 'whatever',
    addToRecent: t.optional(t.boolean, false)
  })
  .views(self => {
    const qString = promisedComputed(null, () => {
      getSnapshot(self)
      return createQueryString(self)
    })
    return {
      get displayString () {
        const parts = (self as any).qString?.split('WHERE')
        if (parts.length > 1) return parts[1]
      },
      get mapping () {
        let mapping: Record<string, any> = { type: 'list' }
        if (hasSoql(self)) {
          mapping = {
            ...mapping,
            bindingType: 'function',
            children: [
              {
                key: 'transformation',
                bindingType: 'value',
                type: 'Transformation',
                value: getSnapshot(self)
              },
              { key: 'data', type: 'list' }
            ],
            dataSource: 'Transformation'
          }
        }
        return mapping // Mapping.create(mapping, { getOrLoadObject: getRoot(self).getOrLoadObject})
      },
      get dataSourceFieldSets () {
        return (self.where && self.where.dataSourceFieldSets) || {}
      },
      get isOnDataSource () {
        let parent = self
        while (parent) {
          parent = hasParent(parent) ? getParent(parent) : null
          if (parent && getType(parent).name === 'Salesforce') return true
        }
        return false
      },
      get parent () {
        return hasParent(self) ? getParent(self) : null
      },
      get tableName () {
        const parent = (self as any).parent
        if (!parent) return self.table
        return (
          parent.sobject ||
          (parent.dataSource && parent.dataSource.sobject) ||
          'unknown'
        )
      },

      get isGroupBy () {
        return self.groupBy?.groupByFields?.length !== 0
      },
      get queryFields () {
        return getTransformationQueryFields(self, (self as any).isOnDataSource)
      },
      get busy () {
        return qString.busy
      },
      get qString () {
        return qString.value
      },
      labels () {
        const childNodes = self.groupBy ? self.groupBy.labels() : []
        return {
          label: '[transformer]',
          key: 'transformer',
          meta: { component: self },
          childNodes: childNodes
        }
      },
      /*
  constructor (t) {
    this.groupBy = new GroupBy(t && t.groupBy)
    this.where = new Where(t && t.where)
    this.table = t && t.table
  }
*/
      transform: data => {
        const fields = [
          ...((self.where && self.where.queryFields) || []),
          ...((self.groupBy && self.groupBy.groupByFields) || []),
          ...((self.groupBy && self.groupBy.validAggFields) || [])
        ]

        return query(createQueryString(self), data, fields)
      }
    }
  })
  .actions(crudActions)


export interface IDataTransformer extends Instance<typeof DataTransformer> {};
export default DataTransformer;

const parseRuleLevels = clause => {
  let currentLevel = 0
  let level = []
  const levels = []
  let connector = ''
  // connector is the AND/OR before a given number
  // level is the nesting depth of the number...
  // ex: 1 AND (2 AND 3 AND (4 OR 5))
  // nesting depth for 1: 0
  // nesting depth for 2: 1
  // nesting depth for 3: 1
  // nesting depth for 4: 2
  // nesting depth for 5: 2

  clause && clause.split('').forEach(char => {
    if (char === '(') {
      if (level !== []) {
        levels.push({ lvl: currentLevel, val: level.slice(0) })
        level = []
      }
      currentLevel += 1
    } else if (char === ')') {
      if (level !== []) {
        levels.push({ lvl: currentLevel, val: level.slice(0) })
        level = []
      }
      currentLevel -= 1
    } else if (!isNaN(char) && char !== '' && char !== ' ') {
      level.push([connector, char])
      connector = ''
    } else {
      connector += char
    }
  })

  if (level.length) levels.push({ lvl: currentLevel, val: level.slice(0) })
  return levels
}

// const filterOutNumber = (removeNum, val) => {
//   return val.filter(([operator, number]) => parseInt(number) !== removeNum)
// }
const updateLevels = (levels, removeNumber) => {
  // takes out removeNumber from each level and updates connectors (AND/OR) where necessary
  // also updates lvl if only one number is left
  const levelsWithoutNumber = levels.map(({ lvl, val }, idx) => {
    // checking if first number block is being removed
    // then saving it's connector as firstConnector
    // so that it can be applied to the filtered val's first block
    let firstConnector
    if (lvl > 0 && val.length && parseInt(val[0][1]) === removeNumber) {
      firstConnector = val[0][0]
    }

    const newVal = val.filter(
      ([operator, number]) => parseInt(number) !== removeNumber
    )

    if (firstConnector) newVal[0][0] = firstConnector

    // check if the level to the right, when removeNumber is filtered,
    // If the next level will go down, don't change current lvl
    // since they will be the same
    if (idx + 1 <= levels.length - 1 && lvl > 0) {
      const nextLevel = levels[idx + 1]
      if (nextLevel.lvl === lvl + 1) {
        const nextNewVal = nextLevel.val.filter(
          ([operator, number]) => parseInt(number) !== removeNumber
        )
        if (nextNewVal.length <= 1) return { lvl, val: newVal }
      }
    }
    const newLvl = lvl > 0 && newVal.length <= 1 ? lvl - 1 : lvl
    return { lvl: newLvl, val: newVal }
  })

  let prevLevel = 0
  let groupedLevel = []
  const groupedLevels = []
  // merges adjacent items with the same level number
  levelsWithoutNumber.forEach(({ lvl, val }) => {
    if (lvl === prevLevel) {
      val.forEach(v => groupedLevel.push(v))
    } else {
      groupedLevels.push({ lvl: prevLevel, val: groupedLevel })
      groupedLevel = val
      prevLevel = lvl
    }
  })

  if (groupedLevel !== []) {
    groupedLevels.push({ lvl: prevLevel, val: groupedLevel })
  }
  return groupedLevels
}

const createClauseString = (groupedLevels, removeNumber) => {
  // updates rule numbers greater than the one that is being removed
  const calcRuleNum = num =>
    parseInt(num) > removeNumber ? `${parseInt(num) - 1}` : num
  let finalClauseStr = ''
  let clause = ''

  groupedLevels.forEach(({ lvl, val }, idx) => {
    if (!val.length) return
    if (idx === 0) {
      if (val[0][0] !== '') {
        val[0][0] = ''
      }
    }

    let lvlDiff

    if (lvl !== 0) {
      // check previous level, and
      // find the diff between current lvl & prev
      // that number determines the qty of opening parens to put
      if (groupedLevels[idx - 1]) {
        const prevLevel = groupedLevels[idx - 1]
        lvlDiff = parseInt(lvl) - parseInt(prevLevel.lvl)
      } else {
        lvlDiff = parseInt(lvl)
      }

      lvlDiff = lvlDiff < 1 ? 1 : lvlDiff

      clause = `${val[0][0]}`
      if (val.length > 1) clause += '('.repeat(lvlDiff)
      clause += `${calcRuleNum(val[0][1])}`

      val.slice(1).forEach(v => {
        clause += v[0] + calcRuleNum(v[1])
      })

      // same as above except for closing parens
      if (groupedLevels[idx + 1]) {
        const nextLevel = groupedLevels[idx + 1]
        lvlDiff = parseInt(nextLevel.lvl) - parseInt(lvl)
        lvlDiff = lvlDiff < 1 ? 1 : lvlDiff
      }

      clause += ')'.repeat(lvlDiff)
      finalClauseStr += clause
    } else {
      clause = ''
      val.forEach(v => {
        clause += v[0] + calcRuleNum(v[1])
      })
      finalClauseStr += clause
    }
  })

  // removing any extra spaces that popped up during the parsing
  finalClauseStr = finalClauseStr && finalClauseStr
    .split(' ')
    .filter(c => c !== '')
    .join(' ')
  return finalClauseStr
}

export const createWhereClauseOrder = (clauseStr, removedNumber) => {
  const ruleLevels = parseRuleLevels(clauseStr)
  const updatedLevels = updateLevels(ruleLevels, removedNumber)
  return createClauseString(updatedLevels, removedNumber)
}

export const createQueryString = config => {
  const {
    table,
    where,
    tableName,
    fields,
    groupBy,
    order,
    limit,
    addToRecent,
    additionalText,
    datasourceType
  } = config
  const isGroupBy =
    groupBy && groupBy.groupByFields && groupBy.groupByFields.length
  const uniqueFields = uniqBy(
    [
      ...((!isGroupBy && fields) || []),
      ...((!isGroupBy && where && getWhereQueryFields(where)) || []),
      ...((groupBy && groupBy.groupByFields) || [])
    ],
    'fieldPath'
  )

  return soqlize({
    sobject: tableName || table,
    fields: [
      ...uniqueFields,
      ...((groupBy && groupBy.validAggFields) || [])
    ].map(f => (isStateTreeNode(f) && getSnapshot(f)) || f),
    groupBy: groupBy && groupBy.groupByFields,
    having: groupBy && groupBy.having && groupBy.having.resolved,
    orderBy: order && order.fields,
    additionalText,
    limit: limit,
    addToRecent,
    where: where?.resolved || where,
    datasourceType
  })
}

export const createOdataString = config => {
  const {
    where,
    order,
    limit
  } = config

  return toOdata({
    limit: limit,
    orderby: order.fields,
    where: where && where.resolved
  })
}

const hasSoql = dt => {
  if (dt.where && dt.where.rules && dt.where.rules.length) return true
  if (dt.groupBy) {
    if (dt.groupBy.aggregateFields && dt.groupBy.aggregateFields.length) {
      return true
    }
    if (dt.groupBy.groupByFields && dt.groupBy.groupByFields.length) return true
  }
  return false
}
